import { useEffect, useState, type FunctionComponent } from "react";
import { useSearchParams } from "react-router-dom";
import StatsHomeDashboardAdminPartial from "../../admin/home/home/stats-home-dashboard.admin.partials";
import type { ICampaignMetricMinimal } from "../../../types/models/campaign-metric.types";
import { Barchart } from "../../../components/charts/bar.chart";
import { Linechart } from "../../../components/charts/line.chart";
import type { ICampaign } from "../../../types/models/campaign.models.types";
import AddImageIcon from "../../../assets/svgs/common/add-image.svg";
import ClockIcon from "../../../assets/svgs/common/clock.svg";
import { formatDate, getBarColor } from "../../../utils/utils";
import millify from "millify";
import { Slider } from "../../../components/ui/slider";

export const ShareMetricsCommonPartial: FunctionComponent = () => {
	const [searchParams] = useSearchParams();
	const data = searchParams.get("campaign");
	const metrics = searchParams.get("metrics");

	const [campaignMetrics, setCampaignMetrics] =
		useState<ICampaignMetricMinimal>(
			JSON.parse(decodeURIComponent(metrics ?? "{}")),
		);
	const [campaignData, setCampaignData] = useState<ICampaign>(
		JSON.parse(decodeURIComponent(data ?? "{}")),
	);

	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	useEffect(() => {
		if (data) {
			setCampaignData(JSON.parse(decodeURIComponent(data)));
		}

		if (metrics) {
			setCampaignMetrics(JSON.parse(decodeURIComponent(metrics)));
		}
	}, [searchParams]);

	const AverageViewsPerPostChartData = [
		{
			category: "Views",
			value: campaignMetrics?.averageViewsPerPost ?? 0,
			fill: getBarColor(1),
		},
		{
			category: "Likes",
			value: campaignMetrics?.averageLikesPerPost ?? 0,
			fill: getBarColor(2),
		},
		{
			category: "Comments",
			value: campaignMetrics?.averageCommentsPerPost ?? 0,
			fill: getBarColor(0),
		},
		{
			category: "Shares",
			value: campaignMetrics?.averageSharesPerPost ?? 0,
			fill: getBarColor(3),
		},
	];

	console.log(campaignMetrics?.totalPosts);

	const ApprovedPostsChartData = [
		{
			category: "Total Posts",
			value: campaignMetrics?.totalPosts ?? 0,
			fill: getBarColor(0),
		},
		{
			category: "Approved Posts",
			value: campaignMetrics?.approvedPosts ?? 0,
			fill: getBarColor(1),
		},
		{
			category: "Rejected Posts",
			value: campaignMetrics?.rejectedPosts ?? 0,
			fill: getBarColor(2),
		},
	];
	const PostPerDayChartData = [
		{
			category: "Total Posts",
			value: millify(campaignMetrics?.totalPosts ?? 0),
		},
	];

	const TotalViewsChartData = [
		{
			category: "Total views",
			value: millify(campaignMetrics?.totalViews ?? 0),
		},
		{
			category: "Cost per view",
			value: `₹ ${(campaignMetrics?.costPerView ?? 0).toFixed(3)}`,
		},
	];

	if (!campaignData || !campaignMetrics) {
		return null;
	}

	return (
		<div className="flex flex-col gap-8 bg-background p-16 w-full">
			<div className="flex gap-6">
				{campaignData?.coverImage?.url ? (
					// biome-ignore lint/a11y/useKeyWithClickEvents: <explanation>
					<img
						className="rounded-lg w-full max-w-md h-60 object-cover"
						src={`${campaignData.coverImage.url}`}
						alt=""
					/>
				) : (
					<div className="flex justify-center items-center border border-border rounded-lg w-full max-w-md h-60">
						<AddImageIcon />
					</div>
				)}

				<div className="flex justify-between items-start w-full">
					<div className="flex flex-col justify-between w-full h-full">
						<div className="flex justify-between items-start w-full">
							<div className="flex flex-col gap-6">
								<div className="flex items-center gap-4">
									<div className="flex items-center gap-4">
										<div className="flex items-center gap-1 px-4 py-2 border border-border rounded-full">
											<ClockIcon />
											<p
												className="font-semibold text-base"
												// biome-ignore lint/security/noDangerouslySetInnerHtml: <explanation>
												dangerouslySetInnerHTML={{
													__html:
														campaignData?.startDate && campaignData.endDate
															? `${formatDate(new Date(campaignData.startDate).toISOString())} - ${formatDate(new Date(campaignData.endDate).toISOString())}`
															: "",
												}}
											/>
										</div>
									</div>
								</div>

								<h1 className="font-semibold text-3xl text-primary">
									{campaignData?.name}
								</h1>
							</div>
						</div>

						<div className="flex flex-col gap-3 w-2/3">
							<div className="flex justify-between items-center w-full">
								<span className="font-semibold text-base text-secondary">
									Budget used:
								</span>
								<span className="font-semibold text-primary text-xl">
									₹ {millify(campaignData?.metrics?.budgetUsed || 0)} of{" "}
									{millify(campaignData?.budget || 0)}
								</span>
							</div>

							<Slider
								stepsVisible={false}
								thumbWidth="30"
								thumbHeight="30"
								trackHeight="h-6"
								max={campaignData?.budget || 0}
								value={[campaignData?.metrics?.budgetUsed || 0]}
							/>
						</div>
					</div>
				</div>
			</div>

			<div className="flex flex-col gap-8">
				<StatsHomeDashboardAdminPartial
					campaignMetrics={campaignMetrics as ICampaignMetricMinimal}
				/>

				<Barchart
					fetchingCampaignMetrics={false}
					campaignMetrics={campaignMetrics as ICampaignMetricMinimal}
					chartData={ApprovedPostsChartData}
					title="Approved posts"
				/>
				<Linechart
					campaignMetrics={campaignMetrics as ICampaignMetricMinimal}
					fetchingCampaignMetrics={false}
					lineData={campaignMetrics?.postsPerDay ?? []}
					chartData={PostPerDayChartData}
					title="Posts per day"
				/>
				<Linechart
					campaignMetrics={campaignMetrics as ICampaignMetricMinimal}
					fetchingCampaignMetrics={false}
					lineData={campaignMetrics?.totalViewsChart ?? []}
					chartData={TotalViewsChartData}
					title="Total views"
				/>
				<Barchart
					fetchingCampaignMetrics={false}
					campaignMetrics={campaignMetrics as ICampaignMetricMinimal}
					chartData={AverageViewsPerPostChartData}
					title="Average views per post"
				/>
			</div>
			{/* <Outlet /> */}
		</div>
	);
};
